/**
 * AttendanceToggle — the 确认出席 cell: a bare checkbox, no label, so the column stays narrow
 * and does not wrap.
 *
 *   approved, and you created it    an enabled checkbox
 *   approved, anyone else           the same checkbox, disabled — the value, read-only
 *   not approved                    a dash: there is no attendance to confirm yet
 *
 * Editable only where the service would accept the change (utils/attendance): the
 * nomination's creator, on an APPROVED nomination.
 *
 * Optimistic: the box flips at once, the request goes out, and the row is replaced with what the
 * service returns. A failure flips it back and says why in place — the cell is the only thing
 * that changed, so that is where the reader is looking.
 *
 * A confirmed box carries "Confirmed by X on <date>" as its hover title (utils/attendance
 * `attendanceConfirmedNote`): who ticked it is an audit question, not worth a column.
 */

import { useState } from 'react';
import Box from '@amzn/awsui-components-react/polaris/box';
import Checkbox from '@amzn/awsui-components-react/polaris/checkbox';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { eventHubCalendarClient, type Nomination } from '../../client/EventCalendarClient';
import {
  attendanceConfirmedNote,
  canConfirmAttendance,
  isAttendanceConfirmed,
} from '../../utils/attendance';
import { guestNameOf } from '../../utils/guestName';

export interface AttendanceToggleProps {
  nomination: Nomination;
  /** The login Event Hub acts as (useEventHubLogin). */
  login: string | undefined;
  /** Receives the nomination as the service returned it, so the page's list stays current. */
  onUpdated: (updated: Nomination) => void;
}

export default function AttendanceToggle({ nomination, login, onUpdated }: AttendanceToggleProps) {
  const confirmed = isAttendanceConfirmed(nomination);
  // What the box shows while a request is in flight; null when it simply mirrors the data.
  const [pending, setPending] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const guest = guestNameOf(nomination.guestInformation) ?? 'this guest';
  const note = attendanceConfirmedNote(nomination);

  if (nomination.status !== 'APPROVED') {
    return <Box color="text-status-inactive">—</Box>;
  }
  if (!canConfirmAttendance(nomination, login)) {
    return (
      <div title={note}>
        <Checkbox
          checked={confirmed}
          disabled
          ariaLabel={`Attendance ${confirmed ? 'confirmed' : 'not confirmed'} for ${guest}`}
        />
      </div>
    );
  }

  async function change(next: boolean) {
    setPending(next);
    setError(null);
    try {
      const updated = await eventHubCalendarClient().updateNominationAttendance(
        nomination.nominationId ?? '',
        next,
      );
      onUpdated(updated);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  }

  return (
    <div title={note}>
      <Checkbox
        checked={pending ?? confirmed}
        disabled={pending !== null}
        onChange={({ detail }) => void change(detail.checked)}
        ariaLabel={`Confirm attendance for ${guest}`}
      />
      {error ? (
        <StatusIndicator type="error">{error}</StatusIndicator>
      ) : null}
    </div>
  );
}
